import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, User, Mail, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { useTicket, useMarkTicketDone, useUpdateTicketStatus } from '../../../hooks/useApi';
import { SupportTicket } from '../../../types';
import ReplyTicketModal from './ReplyTicketModal';
import { PageHeader } from '@kioskgaming/ui';
import { useFormatDate } from '../../../hooks/useFormatDate';

interface TicketDetailsProps {
  ticket: SupportTicket | null;
  onBack: () => void;
}

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800', icon: MessageSquare },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800', icon: XCircle },
} as const;

const TicketDetails: React.FC<TicketDetailsProps> = ({ ticket, onBack }) => {
  const fmt = useFormatDate();
  const [showReplyModal, setShowReplyModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: ticketData, isLoading } = useTicket(ticket?.ticketNumber || '');
  const markDoneMutation = useMarkTicketDone();
  const updateStatusMutation = useUpdateTicketStatus();

  const currentTicket = ticketData?.data || ticket;

  // Auto-scroll to latest message
  useEffect(() => {
    if (currentTicket?.replies) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentTicket?.replies]);

  if (!currentTicket) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No ticket selected</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const statusInfo =
    statusConfig[currentTicket.status as keyof typeof statusConfig] || {
      label: currentTicket.status,
      color: 'bg-gray-100 text-gray-800',
      icon: MessageSquare,
    };
  const StatusIcon = statusInfo.icon;
  const canReply = currentTicket.status === 'pending' || currentTicket.status === 'in_progress';
  const canMarkResolved = currentTicket.status === 'pending' || currentTicket.status === 'in_progress';
  const canMarkClosed = currentTicket.status === 'resolved';

  const handleMarkResolved = () => {
    if (!currentTicket) return;
    updateStatusMutation.mutate({
      ticketNumber: currentTicket.ticketNumber,
      data: {
        status: 'resolved',
        notes: currentTicket.customerServiceNotes || '',
      },
    });
  };

  const handleMarkClosed = () => {
    if (!currentTicket) return;
    markDoneMutation.mutate(currentTicket.ticketNumber);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        back={{ onClick: onBack, label: 'Back' }}
        title={currentTicket.ticketNumber}
        description={
          <div className="mt-1 flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
              <StatusIcon className="h-3 w-3 inline mr-1" />
              {statusInfo.label}
            </span>
            {currentTicket.estimatedResponseTime ? (
              <span className="text-sm text-muted-foreground">
                Response time: {currentTicket.estimatedResponseTime}
              </span>
            ) : null}
          </div>
        }
      />

      {/* Ticket Information */}
      <Card>
        <CardHeader>
          <CardTitle>Ticket Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Email:</span>
                <span>{currentTicket.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Phone:</span>
                <span>{currentTicket.phoneNumber}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Created:</span>
                <span>{fmt(currentTicket.createdAt)}</span>
              </div>
              {currentTicket.resolvedAt && currentTicket.status === 'resolved' && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Resolved:</span>
                  <span>{fmt(currentTicket.resolvedAt)}</span>
                </div>
              )}
              {currentTicket.resolvedAt && currentTicket.status === 'closed' && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Closed:</span>
                  <span>{fmt(currentTicket.resolvedAt)}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <span className="font-medium">Category:</span>
            <span className="ml-2">{currentTicket.categoryDisplayName}</span>
          </div>

          <div>
            <span className="font-medium">Message:</span>
            <p className="mt-2 p-3 bg-gray-50 rounded-lg">{currentTicket.message}</p>
          </div>

          {currentTicket.customerServiceNotes && (
            <div>
              <span className="font-medium">Admin Notes:</span>
              <p className="mt-2 p-3 bg-blue-50 rounded-lg">{currentTicket.customerServiceNotes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Replies */}
      <Card>
        <CardHeader>
          <CardTitle>Conversation</CardTitle>
        </CardHeader>
        <CardContent>
          {currentTicket.replies && currentTicket.replies.length > 0 ? (
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {currentTicket.replies.map((reply) => (
                <div
                  key={reply.id}
                  className={`p-4 rounded-lg ${reply.replyType === 'admin'
                    ? 'bg-blue-50 border-l-4 border-blue-500'
                    : 'bg-gray-50 border-l-4 border-gray-300'
                    }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span className="font-medium">
                        {reply.replyType === 'admin' ? reply.adminName : 'User'}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        ({reply.replyType === 'admin' ? 'Admin' : 'User'})
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {fmt(reply.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm">{reply.message}</p>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4" />
              <p>No replies yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-2">
        {canReply && (
          <Button onClick={() => setShowReplyModal(true)}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Reply
          </Button>
        )}
        {canMarkResolved && (
          <Button
            variant="outline"
            onClick={handleMarkResolved}
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Resolved
              </>
            )}
          </Button>
        )}
        {canMarkClosed && (
          <Button
            variant="outline"
            onClick={handleMarkClosed}
            disabled={markDoneMutation.isLoading}
          >
            {markDoneMutation.isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Closed
              </>
            )}
          </Button>
        )}
      </div>

      {/* Modals */}
      {showReplyModal && (
        <ReplyTicketModal
          ticket={currentTicket}
          isOpen={showReplyModal}
          onClose={() => setShowReplyModal(false)}
          onSuccess={() => {
            setShowReplyModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TicketDetails;
